import { Content, DefaultPage } from '@shared/layout/DefaultPage'
import { Header } from './ui/Header'
import { Centered } from '@/shared/layout/Centered'
import { RoundInput } from './ui/RoundInput'
import { ExecutionInput } from './ui/ExecutionInput'
import { PresetGrid } from './ui/PresetGrid'
import {
	buildTimerPresetId,
	useTimerPresets,
	areTimerPresetsEqual,
	type TimerPreset,
} from './lib/useTimerPresets'
import { useState, useMemo, useCallback, type Dispatch, type SetStateAction } from 'react'
import { useIonRouter } from '@ionic/react'
import { ROUTES } from '@/shared/constants/routes'
import { Bookmark, Clock3 } from 'lucide-react'

type ChangeType = 'increase' | 'decrease'

export const TimerPage = () => {
	const [roundsAmount, setRoundsAmount] = useState<number>(9)
	const [approachTime, setApproachTime] = useState<number>(20)
	const [restTime, setRestTime] = useState<number>(20)

	const totalRestDuration = roundsAmount * restTime
	const totalSeconds = roundsAmount * approachTime + totalRestDuration
	const formatSeconds = useCallback((seconds: number) => {
		const minutes = Math.floor(seconds / 60)
		const remSeconds = seconds % 60
		return `${String(minutes).padStart(2, '0')}:${String(remSeconds).padStart(2, '0')}`
	}, [])

	const totalTimeLabel = formatSeconds(totalSeconds)

	const { presets, savePreset, removePreset } = useTimerPresets()

	const currentPreset = useMemo(
		() => ({
			rounds: roundsAmount,
			approach: approachTime,
			rest: restTime,
			totalSeconds,
		}),
		[approachTime, restTime, roundsAmount, totalSeconds]
	)

	const isCurrentPresetSaved = useMemo(
		() => presets.some(preset => areTimerPresetsEqual(preset, currentPreset)),
		[presets, currentPreset]
	)

	const router = useIonRouter()
	const presetId = useMemo(() => buildTimerPresetId(currentPreset), [currentPreset])
	const goToPresetPage = useCallback(() => {
		router.push(ROUTES.overall.timerDetail.replace(':presetId', presetId))
	}, [router, presetId])

	const handleBookmarkToggle = useCallback(() => {
		if (isCurrentPresetSaved) {
			removePreset(currentPreset)
			return
		}
		savePreset(currentPreset)
	}, [currentPreset, isCurrentPresetSaved, removePreset, savePreset])

	const handlePresetSelect = useCallback((preset: TimerPreset) => {
		setRoundsAmount(preset.rounds)
		setApproachTime(preset.approach)
		setRestTime(preset.rest)
	}, [])

	const presetMatchesCurrent = useCallback(
		(preset: TimerPreset) => areTimerPresetsEqual(preset, currentPreset),
		[currentPreset]
	)

	const onRoundsChange = (type: ChangeType) => {
		if (type === 'increase') {
			if (roundsAmount >= 50) {
				return
			}
			setRoundsAmount(prev => prev + 1)
		} else {
			if (roundsAmount <= 1) {
				return
			}
			setRoundsAmount(prev => prev - 1)
		}
	}

	const onExecutionChange = (
		type: ChangeType,
		currentValue: number,
		setter: Dispatch<SetStateAction<number>>
	) => {
		if (type === 'increase') {
			if (currentValue >= 300) {
				return
			}
			setter(prev => prev + 1)
		} else {
			if (currentValue <= 1) {
				return
			}
			setter(prev => prev - 1)
		}
	}

	const bookmarkLabel = isCurrentPresetSaved
		? `Текущий таймер сохранён (${totalTimeLabel})`
		: `Сохранить таймер (${totalTimeLabel})`

	return (
		<DefaultPage noPadding>
			<Content className='app-bottom-nav-content'>
				<Header />
				<Centered className='flex-col px-[20px] pb-[calc(var(--app-bottom-nav-reserve)+12px)] pt-5'>
					<div className='mb-5 w-full max-w-[380px] text-center text-[15px] font-medium leading-[1.25] text-[#7F7F7F]'>
						Настройте интервалы и начните тренировку
					</div>
					<RoundInput
						className='w-full'
						onDecrease={() => onRoundsChange('decrease')}
						onIncrease={() => onRoundsChange('increase')}
						valueToShow={String(roundsAmount)}
					/>
					<ExecutionInput
						className='mt-3'
						approachValue={formatSeconds(approachTime)}
						restValue={formatSeconds(restTime)}
						onApproachDecrease={() =>
							onExecutionChange('decrease', approachTime, setApproachTime)
						}
						onApproachIncrease={() =>
							onExecutionChange('increase', approachTime, setApproachTime)
						}
						onRestDecrease={() => onExecutionChange('decrease', restTime, setRestTime)}
						onRestIncrease={() => onExecutionChange('increase', restTime, setRestTime)}
					/>
					<div className='mt-3 flex w-full items-center justify-between rounded-[20px] border border-[#EFEFEF] bg-[#F8F8FA] px-5 py-2.5'>
						<div className='flex items-center gap-2 text-[14px] font-medium uppercase text-[#8B8B8B]'>
							<Clock3 aria-hidden='true' className='size-[17px] text-[#6C71FF]' />
							<span>Всего время</span>
						</div>
						<div className='text-[32px] font-semibold leading-none tracking-[-0.03em]'>
							{totalTimeLabel}
						</div>
					</div>
					<div className='mt-4 flex w-full items-center gap-3'>
						<button
							type='button'
							onClick={goToPresetPage}
							className='h-[54px] flex-1 rounded-[30px] bg-[#D2F300] text-[20px] font-semibold text-black transition hover:brightness-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black'>
							Начать
						</button>
						<button
							type='button'
							onClick={handleBookmarkToggle}
							aria-pressed={isCurrentPresetSaved}
							aria-label={bookmarkLabel}
							title={bookmarkLabel}
							className='flex h-[54px] w-[94px] items-center justify-center rounded-[30px] bg-black text-white transition hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black'>
							<Bookmark
								className='size-6 text-white'
								strokeWidth={2.2}
								stroke='white'
								fill='none'
								aria-hidden='true'
							/>
						</button>
					</div>
					<PresetGrid
						presets={presets}
						onSelect={handlePresetSelect}
						isActive={presetMatchesCurrent}
						formatSeconds={formatSeconds}
					/>
				</Centered>
			</Content>
		</DefaultPage>
	)
}
